import React, { useState } from 'react';
import {
    Bot,
    Activity,
    Database,
    Zap,
    ArrowRight,
    CheckCircle,
    Terminal,
    Code,
    ExternalLink
} from 'lucide-react';
import { DUCKMON_AGENTS, ECOSYSTEM_ARCHITECTURE, AIAgent } from '../../types/agent';

// Agent Card Component
const AgentInfoCard: React.FC<{ agent: AIAgent }> = ({ agent }) => {
    const statusColors: Record<string, string> = {
        running: '#22c55e',
        stopped: '#ef4444',
    };

    const typeIcons: Record<string, string> = {
        trading: '📊',
        prediction: '🎯',
        analysis: '📈',
        security: '🛡️',
        mev: '⚡',
        monitoring: '👁️',
    };

    return (
        <div className="agent-info-card">
            <div className="agent-info-header">
                <span className="agent-type-icon">{typeIcons[agent.type]}</span>
                <div className="agent-title-group">
                    <h4>{agent.name}</h4>
                    <span className="agent-version">v{agent.version}</span>
                </div>
                <span
                    className="agent-status-dot"
                    style={{ backgroundColor: statusColors[agent.status] }}
                    title={agent.status.toUpperCase()}
                />
            </div>
            <p className="agent-description">{agent.description}</p>
            <div className="agent-features">
                <h5>Features</h5>
                <ul>
                    {agent.features.map((feature, i) => (
                        <li key={i}>
                            <CheckCircle size={12} />
                            {feature}
                        </li>
                    ))}
                </ul>
            </div>
            <div className="agent-indicators">
                <h5>Indicators</h5>
                <div className="indicator-tags">
                    {agent.indicators.map((indicator, i) => (
                        <span key={i} className="indicator-tag">{indicator}</span>
                    ))}
                </div>
            </div>
            <div className="agent-meta">
                <span>⏱️ Updates every {agent.updateInterval}s</span>
                {agent.onChainEnabled && <span>⛓️ On-chain enabled</span>}
            </div>
        </div>
    );
};

// Data Flow Diagram
const DataFlowDiagram: React.FC = () => {
    return (
        <div className="data-flow-diagram">
            <h3>
                <Zap size={20} />
                How It Works
            </h3>
            <div className="flow-steps">
                {ECOSYSTEM_ARCHITECTURE.dataFlow.map((step, index) => (
                    <React.Fragment key={step.step}>
                        <div className="flow-step">
                            <div className="step-icon">{step.icon}</div>
                            <div className="step-content">
                                <span className="step-number">Step {step.step}</span>
                                <h4>{step.title}</h4>
                                <p>{step.description}</p>
                            </div>
                        </div>
                        {index < ECOSYSTEM_ARCHITECTURE.dataFlow.length - 1 && (
                            <div className="flow-arrow">
                                <ArrowRight size={24} />
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

// Tech Stack Component
const TechStack: React.FC = () => {
    const { technology } = ECOSYSTEM_ARCHITECTURE;

    return (
        <div className="tech-stack-component">
            <h3>
                <Code size={20} />
                Technology Stack
            </h3>
            <div className="tech-categories">
                <div className="tech-category">
                    <h4>🤖 Agents</h4>
                    <div className="tech-items">
                        {technology.agents.map((tech, i) => (
                            <span key={i} className="tech-item">{tech}</span>
                        ))}
                    </div>
                </div>
                <div className="tech-category">
                    <h4>🖥️ Frontend</h4>
                    <div className="tech-items">
                        {technology.frontend.map((tech, i) => (
                            <span key={i} className="tech-item">{tech}</span>
                        ))}
                    </div>
                </div>
                <div className="tech-category">
                    <h4>⛓️ Blockchain</h4>
                    <div className="tech-items">
                        {technology.blockchain.map((tech, i) => (
                            <span key={i} className="tech-item">{tech}</span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Contract Info
const ContractInfo: React.FC = () => {
    const { contracts } = ECOSYSTEM_ARCHITECTURE;

    return (
        <div className="contract-info-component">
            <h3>
                <Database size={20} />
                Smart Contracts
            </h3>
            <div className="contracts-list">
                {contracts.map((contract, i) => (
                    <div key={i} className="contract-card">
                        <div className="contract-header">
                            <h4>{contract.name}</h4>
                            <span className="contract-address">{contract.address}</span>
                        </div>
                        <div className="contract-functions">
                            {contract.functions.map((func, j) => (
                                <code key={j}>{func}()</code>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Run Agents Guide
const RunAgentsGuide: React.FC = () => {
    const [copied, setCopied] = useState('');

    const copyCommand = (cmd: string, id: string) => {
        navigator.clipboard.writeText(cmd);
        setCopied(id);
        setTimeout(() => setCopied(''), 2000);
    };

    const commands = [
        { id: 'all', label: 'Run All Agents', cmd: 'cd agents && node orchestrator.mjs' },
        { id: 'oracle', label: 'Trading Oracle', cmd: 'cd agents/trading-oracle && npm start' },
        { id: 'prediction', label: 'Prediction Bot', cmd: 'cd agents/prediction-bot && npm start' },
        { id: 'analyzer', label: 'Market Analyzer', cmd: 'cd agents/market-analyzer && npm start' },
    ];

    return (
        <div className="run-agents-guide">
            <h3>
                <Terminal size={20} />
                Run the Agents
            </h3>
            <p className="guide-intro">
                Agents are Node.js applications that run independently. They analyze the market and post signals to the blockchain.
            </p>
            <div className="command-list">
                {commands.map(({ id, label, cmd }) => (
                    <div key={id} className="command-item">
                        <span className="command-label">{label}</span>
                        <div className="command-box">
                            <code>{cmd}</code>
                            <button
                                onClick={() => copyCommand(cmd, id)}
                                className={copied === id ? 'copied' : ''}
                            >
                                {copied === id ? '✓' : 'Copy'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="guide-note">
                <strong>Note:</strong> Make sure to set <code>PRIVATE_KEY</code> in your <code>.env</code> file for blockchain writes.
            </div>
        </div>
    );
};

// Main Component
const AgentArchitecture: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'agents' | 'flow' | 'tech' | 'run'>('agents');

    const tabs = [
        { id: 'agents', label: 'AI Agents', icon: Bot },
        { id: 'flow', label: 'How It Works', icon: Activity },
        { id: 'tech', label: 'Technology', icon: Code },
        { id: 'run', label: 'Run Agents', icon: Terminal },
    ];

    return (
        <div className="agent-architecture">
            <div className="architecture-header">
                <h2>
                    <Bot size={28} />
                    DUCKMON Agent Ecosystem
                </h2>
                <p>Intelligent AI agents that analyze markets and post signals on-chain</p>
            </div>

            <div className="architecture-tabs">
                {tabs.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        className={`arch-tab ${activeTab === id ? 'active' : ''}`}
                        onClick={() => setActiveTab(id as typeof activeTab)}
                    >
                        <Icon size={16} />
                        {label}
                    </button>
                ))}
            </div>

            <div className="architecture-content">
                {activeTab === 'agents' && (
                    <div className="agents-grid">
                        {DUCKMON_AGENTS.map((agent) => (
                            <AgentInfoCard key={agent.id} agent={agent} />
                        ))}
                    </div>
                )}

                {activeTab === 'flow' && <DataFlowDiagram />}

                {activeTab === 'tech' && (
                    <div className="tech-content">
                        <TechStack />
                        <ContractInfo />
                    </div>
                )}

                {activeTab === 'run' && <RunAgentsGuide />}
            </div>

            <div className="architecture-footer">
                <a
                    href="https://github.com/yeddevall/duckmon-agents-ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="github-link"
                >
                    <ExternalLink size={14} />
                    View Source on GitHub
                </a>
            </div>
        </div>
    );
};

export default AgentArchitecture;
